/**
 * The report's Comments panel, as in AgencyAnalytics: internal threads that
 * clients never see, pinned to a page or a widget, with replies, @mentions,
 * and Resolve / Reopen. Open and Resolved threads are listed separately.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { CommentComposer } from './CommentComposer.tsx';
import {
  commentsApi,
  type CommentThread,
  type ReportComment,
  type StaffMember,
} from './comments-api.ts';
import type { Page, Widget } from './reports-api.ts';

const when = new Intl.DateTimeFormat('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });

function CommentBody(props: { comment: ReportComment; onDelete: () => void }): ReactNode {
  const { comment } = props;
  return (
    <div className="rcm-comment">
      <div className="rcm-meta">
        <strong>{comment.author_name}</strong>
        <time dateTime={comment.created_at}>{when.format(new Date(comment.created_at))}</time>
        {comment.can_delete && (
          <button type="button" className="rcm-link" onClick={props.onDelete}>
            Delete
          </button>
        )}
      </div>
      {comment.deleted ? (
        <p className="rcm-deleted">This comment was deleted.</p>
      ) : (
        <p className="rcm-text">{comment.body}</p>
      )}
    </div>
  );
}

export function CommentsPanel({
  organisationId,
  documentId,
  pages,
  pageIndex,
  selectedWidget,
  onGoToPage,
  onClose,
}: {
  organisationId: string;
  documentId: string;
  pages: Page[];
  pageIndex: number;
  selectedWidget: Widget | undefined;
  onGoToPage: (index: number) => void;
  onClose: () => void;
}): ReactNode {
  const [threads, setThreads] = useState<CommentThread[] | null>(null);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [showResolved, setShowResolved] = useState(false);
  const [onWidget, setOnWidget] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const page = pages[pageIndex];

  const reload = useCallback(
    () =>
      commentsApi.list(organisationId, documentId).then(setThreads, () => {
        setThreads([]);
        setProblem('Comments could not be loaded.');
      }),
    [organisationId, documentId],
  );
  useEffect(() => {
    void reload();
    commentsApi.staff(organisationId).then(setStaff, () => setStaff([]));
  }, [organisationId, reload]);

  const act = (work: Promise<unknown>) =>
    work.then(reload, (error: unknown) =>
      setProblem(error instanceof Error ? error.message : 'That did not work. Try again.'),
    );
  const pinLabel = (thread: CommentThread) => {
    const at = pages.findIndex((item) => item.id === thread.page_id);
    if (at < 0) return thread.page_id ? 'A removed page' : 'Whole report';
    const widget = pages[at]?.widgets.find((item) => item.id === thread.widget_id);
    const name = pages[at]?.title || `Page ${at + 1}`;
    if (!thread.widget_id) return name;
    return `${name} · ${widget ? (widget.display.title ?? widget.type.toLowerCase()) : 'a removed widget'}`;
  };
  const shown = (threads ?? []).filter((thread) => Boolean(thread.resolved_at) === showResolved);
  const openCount = (threads ?? []).filter((thread) => !thread.resolved_at).length;

  return (
    <aside className="rs rcm" aria-label="Comments">
      <header className="rs-head">
        <strong>Comments</strong>
        <button type="button" className="rb-icon" aria-label="Close" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="rs-body">
        <p className="rs-explain">Only your team sees comments. Clients never do.</p>
        <CommentComposer
          staff={staff}
          placeholder="Add a comment. Type @ to mention someone"
          submitLabel="Comment"
          onSubmit={(input) =>
            commentsApi
              .add(organisationId, documentId, {
                ...input,
                ...(page?.id ? { page_id: page.id } : {}),
                ...(onWidget && selectedWidget?.id ? { widget_id: selectedWidget.id } : {}),
              })
              .then(reload)
          }
        />
        {selectedWidget?.id && (
          <label className="rs-check">
            <input
              type="checkbox"
              checked={onWidget}
              onChange={(event) => setOnWidget(event.target.checked)}
            />
            Pin to the selected widget
          </label>
        )}
        <div className="rb-seg" role="group" aria-label="Which comments">
          <button
            type="button"
            className={showResolved ? '' : 'on'}
            aria-pressed={!showResolved}
            onClick={() => setShowResolved(false)}
          >
            Open ({openCount})
          </button>
          <button
            type="button"
            className={showResolved ? 'on' : ''}
            aria-pressed={showResolved}
            onClick={() => setShowResolved(true)}
          >
            Resolved ({(threads?.length ?? 0) - openCount})
          </button>
        </div>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        {threads === null && <p className="rs-explain">Loading…</p>}
        {threads !== null && shown.length === 0 && (
          <p className="rs-explain">
            {showResolved ? 'Nothing resolved yet.' : 'No open comments.'}
          </p>
        )}
        <ul className="rcm-threads">
          {shown.map((thread) => {
            const at = pages.findIndex((item) => item.id === thread.page_id);
            return (
              <li key={thread.id} className="rcm-thread">
                <button
                  type="button"
                  className="rcm-link rcm-pin"
                  disabled={at < 0}
                  onClick={() => onGoToPage(at)}
                >
                  {pinLabel(thread)}
                </button>
                <CommentBody
                  comment={thread}
                  onDelete={() => act(commentsApi.remove(organisationId, thread.id))}
                />
                {thread.replies.map((reply) => (
                  <CommentBody
                    key={reply.id}
                    comment={reply}
                    onDelete={() => act(commentsApi.remove(organisationId, reply.id))}
                  />
                ))}
                {!thread.resolved_at && (
                  <CommentComposer
                    staff={staff}
                    placeholder="Reply"
                    submitLabel="Reply"
                    onSubmit={(input) =>
                      commentsApi
                        .add(organisationId, documentId, { ...input, parent_id: thread.id })
                        .then(reload)
                    }
                  />
                )}
                <button
                  type="button"
                  className="btn small secondary"
                  onClick={() =>
                    act(commentsApi.resolve(organisationId, thread.id, !thread.resolved_at))
                  }
                >
                  {thread.resolved_at ? 'Reopen' : 'Resolve'}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </aside>
  );
}
